import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { 
  Barcode, 
  Camera, 
  X, 
  Check,
  Warning,
  SpinnerGap,
  Plus
} from "@phosphor-icons/react";

const BarcodeScanner = ({ onProductFound, onClose }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scannedCode, setScannedCode] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [manualBarcode, setManualBarcode] = useState("");
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    setError(null);
    setScanning(true);
    
    try {
      html5QrCodeRef.current = new Html5Qrcode("barcode-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 100 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        onScanError
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      setError("Impossible d'accéder à la caméra. Vérifie les permissions.");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    setScannedCode(decodedText);
    await lookupBarcode(decodedText);
  };

  const onScanError = (errorMessage) => {
    // Ignore scan errors (they occur continuously when no barcode is in view)
  };

  const lookupBarcode = async (barcode) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/barcode/${barcode}`
      );
      const data = await response.json();
      
      if (data.found) {
        setProduct(data.product);
      } else {
        setError("Produit non trouvé dans la base de données. Essayez un autre code-barres ou ajoutez-le manuellement.");
      }
    } catch (err) {
      console.error("Error looking up barcode:", err);
      setError("Erreur de connexion. Vérifie ta connexion internet.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualLookup = async () => {
    if (manualBarcode.length >= 8) {
      setScannedCode(manualBarcode);
      await lookupBarcode(manualBarcode);
    }
  };

  const handleAddProduct = () => {
    if (product) {
      onProductFound({
        name: `${product.name}${product.brand ? ` - ${product.brand}` : ''}`,
        barcode: product.barcode,
        calories: product.nutrition_per_100g.calories,
        protein: product.nutrition_per_100g.protein,
        carbs: product.nutrition_per_100g.carbs,
        fat: product.nutrition_per_100g.fat,
        fiber: product.nutrition_per_100g.fiber,
        serving_size: product.serving_size || "100g",
        quantity: quantity,
        image_url: product.image_url,
        nutriscore: product.nutriscore,
      });
    }
  };

  const resetScanner = () => {
    setScannedCode(null);
    setProduct(null);
    setError(null);
    setQuantity(1);
  };

  const getNutriscoreColor = (grade) => {
    const colors = {
      'A': 'bg-green-500',
      'B': 'bg-lime-500',
      'C': 'bg-yellow-500',
      'D': 'bg-orange-500',
      'E': 'bg-red-500',
    };
    return colors[grade] || 'bg-gray-400';
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-charcoal">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <Barcode size={24} className="text-gold" />
          Scanner de code-barres
        </h2>
        <button 
          onClick={onClose}
          className="p-2 text-white hover:text-gold transition-colors"
          data-testid="close-scanner"
        >
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-y-auto">
        {/* Scanner View */}
        {!scannedCode && !product && (
          <div className="w-full max-w-md">
            {/* Camera Scanner */}
            <div 
              id="barcode-reader" 
              ref={scannerRef}
              className={`w-full bg-charcoal rounded-2xl overflow-hidden ${scanning ? 'min-h-[300px]' : ''}`}
            />

            {!scanning && (
              <div className="text-center mt-6">
                <button
                  onClick={startScanner}
                  className="btn-primary flex items-center gap-2 mx-auto mb-6"
                  data-testid="start-scanner"
                >
                  <Camera size={24} />
                  Activer la caméra
                </button>

                <div className="text-gray-400 text-sm mb-4">ou</div>

                {/* Manual Barcode Input */}
                <div className="bg-white rounded-2xl p-4">
                  <p className="text-charcoal font-medium mb-3">Entrer le code manuellement</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={manualBarcode}
                      onChange={(e) => setManualBarcode(e.target.value.replace(/\D/g, ''))}
                      placeholder="Ex: 3017620422003"
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-charcoal"
                      data-testid="manual-barcode-input"
                    />
                    <button
                      onClick={handleManualLookup}
                      disabled={manualBarcode.length < 8}
                      className="px-4 py-3 bg-gold text-charcoal rounded-xl font-semibold disabled:opacity-50"
                      data-testid="manual-lookup-button"
                    >
                      Rechercher
                    </button>
                  </div>
                </div>
              </div>
            )}

            {scanning && (
              <div className="text-center mt-4">
                <p className="text-white mb-2">Placez le code-barres devant la caméra</p>
                <button
                  onClick={stopScanner}
                  className="text-gold underline"
                  data-testid="stop-scanner"
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center">
            <SpinnerGap size={48} className="text-gold animate-spin mx-auto mb-4" />
            <p className="text-white">Recherche du produit...</p>
            <p className="text-gray-400 text-sm mt-1">Code: {scannedCode}</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="w-full max-w-md bg-white rounded-2xl p-6 text-center">
            <Warning size={48} className="text-amber-500 mx-auto mb-4" />
            <p className="text-charcoal font-medium mb-2">Oups !</p>
            <p className="text-gray-500 mb-4">{error}</p>
            {scannedCode && (
              <p className="text-xs text-gray-400 mb-4">Code scanné: {scannedCode}</p>
            )}
            <button
              onClick={resetScanner}
              className="btn-primary"
              data-testid="retry-scanner"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Product Found */}
        {product && !loading && (
          <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden" data-testid="scanned-product">
            {/* Product Header */}
            <div className="p-4 bg-green-50 flex items-center gap-3">
              <Check size={24} className="text-green-500" />
              <span className="text-green-700 font-medium">Produit trouvé !</span>
            </div>

            {/* Product Info */}
            <div className="p-4">
              <div className="flex gap-4 mb-4">
                {product.image_url && (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-20 h-20 object-contain rounded-lg bg-gray-50"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-charcoal">{product.name}</h3>
                  {product.brand && (
                    <p className="text-sm text-gray-500">{product.brand}</p>
                  )}
                  {product.nutriscore && (
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-xs font-bold mt-2 ${getNutriscoreColor(product.nutriscore)}`}>
                      Nutri-Score {product.nutriscore}
                    </div>
                  )}
                </div>
              </div>

              {/* Nutrition Info */}
              <div className="bg-cream rounded-xl p-4 mb-4">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Valeurs pour 100g</p>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div>
                    <p className="text-lg font-bold text-gold">{product.nutrition_per_100g.calories}</p>
                    <p className="text-xs text-gray-500">kcal</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-red-500">{product.nutrition_per_100g.protein}g</p>
                    <p className="text-xs text-gray-500">Prot.</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-teal-500">{product.nutrition_per_100g.carbs}g</p>
                    <p className="text-xs text-gray-500">Gluc.</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-amber-500">{product.nutrition_per_100g.fat}g</p>
                    <p className="text-xs text-gray-500">Lip.</p>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-charcoal font-medium">Quantité (portions)</span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}
                    className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-charcoal hover:border-gold flex items-center justify-center"
                    data-testid="scanner-decrease-quantity"
                  >
                    -
                  </button>
                  <span className="font-bold text-charcoal w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 0.5)}
                    className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-charcoal hover:border-gold flex items-center justify-center"
                    data-testid="scanner-increase-quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={resetScanner}
                  className="flex-1 btn-secondary"
                  data-testid="scan-another"
                >
                  Scanner un autre
                </button>
                <button
                  onClick={handleAddProduct}
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                  data-testid="add-scanned-product"
                >
                  <Plus size={20} />
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="p-4 bg-charcoal text-center">
        <p className="text-gray-400 text-xs">
          Données nutritionnelles fournies par Open Food Facts
        </p>
      </div>
    </div>
  );
};

export default BarcodeScanner;
